/**
 * Sync engine — push/pull orchestration between the local event log and the
 * server's `/api/v2/sync` endpoint.
 *
 * Ported from legacy `frontend/src/services/syncServiceV2.ts::performSyncV2` (now `backend/src/household/syncService.ts`),
 * refactored against the Code Portability Contract:
 *
 *   - CP1: no platform imports, no globals. `fetch`, `navigator.onLine`,
 *     clocks, ids — all injected.
 *   - CP2: storage access goes through `IStorage`; no Dexie imports.
 *   - CP4: sync/LWW logic lives here only, never duplicated in apps.
 *
 * The engine is a plain class wired at app entry. No module-level state.
 * Call `createSyncEngine(deps)` to get one.
 *
 * Legacy behavior preserved:
 *   - On push: all pending events are sent in one batched request
 *     (matches legacy single-POST semantics). The server processes them
 *     in order, records server-side events, and returns newEvents + conflicts.
 *   - Events are marked `synced: true` only after the server acks success.
 *     Failed push leaves events pending for retry.
 *   - On pull: read sync cursor from storage, POST (not GET — same endpoint
 *     is used for both directions; push is a zero-event sync when no events
 *     are pending). Server returns events since the cursor; engine applies
 *     each to local storage with LWW. Cursor advances on success.
 *   - `fullResyncRequired` short-circuits pull: server tells us our cursor
 *     is too old, so we clear local domain data and re-download via
 *     `/api/v2/sync/full-resync`.
 *   - After a successful sync, synced events are garbage-collected from the
 *     pending log (`purgeSyncedEvents`) to prevent unbounded growth.
 */

import type {
  IHouseholdProductStorage,
  IShoppingListItemStorage,
  IShoppingListStorage,
  IStorage,
  ISyncStateStorage,
  PendingEvent,
} from '@shopista/storage/interface';
import type { HouseholdProduct, ShoppingListItem } from '../types';
import {
  type ActionTypeV2,
  type ClientEventV2,
  type HouseholdActionTypeV2,
  type ItemActionTypeV2,
  type ProductActionTypeV2,
  type ServerEventV2,
  type SyncConflictV2,
  type SyncRequestV2,
  type SyncResponseV2,
  markEventsPushed,
  purgeSyncedEvents,
  readPendingEvents,
  readSyncCursor,
  toClientEvent,
  writeSyncCursor,
} from './events';
import { shouldApplyRemote } from './lww';
import { PRODUCT_UPDATED_FIELDS } from './productUpdatedFields';

const HOUSEHOLD_ACTION_TYPES: readonly HouseholdActionTypeV2[] = [
  'household_store_preferences_changed',
];

function isHouseholdActionType(actionType: ActionTypeV2): actionType is HouseholdActionTypeV2 {
  return (HOUSEHOLD_ACTION_TYPES as readonly string[]).includes(actionType);
}

/**
 * Outcome of applying one shopping-list-item event to local storage.
 *
 * `changed` and `invalidate` are deliberately separate (BUG21):
 *   - `changed`   — a local (Dexie) projection write happened.
 *   - `invalidate` — the REST `ShoppingLists.active`/`all` cache the UI renders
 *     must be refetched. The REST cache is a SEPARATE store from local storage
 *     (full snapshot vs. cursor-built), so a relevant event for a row absent
 *     from local storage still needs the REST cache refreshed even though no
 *     local write occurred.
 *
 * Invariant: `invalidate` is never true without the event having passed LWW.
 */
interface ItemApplyResult {
  changed: boolean;
  invalidate: boolean;
}

/** No local write and no REST invalidation (LWW lost, duplicate add, no target). */
const NO_APPLY: ItemApplyResult = { changed: false, invalidate: false };
/** A local write happened; also invalidate the REST cache. */
const APPLIED: ItemApplyResult = { changed: true, invalidate: true };
/** No local write (row absent), but the REST cache may still hold the row. */
const INVALIDATE_ONLY: ItemApplyResult = { changed: false, invalidate: true };

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Authentication + device identity the engine needs to build a sync request.
 * Resolved once per invocation so the engine stays stateless between calls.
 */
export interface SyncIdentity {
  /** Tenant scope — all events belong to this household. */
  householdId: string;
  /** Current user id — stamped on events the engine applies. */
  userId: string;
  /** Device id — persistent across sessions; the server uses it to
   *  exclude a device's own events from its own pull. */
  deviceId: string;
}

/** Minimal sync HTTP surface. Implement with the T3 HttpClient or a test double. */
export interface SyncApi {
  /** POST /api/v2/sync — push client events + pull server events in one call. */
  sync(request: SyncRequestV2): Promise<SyncResponseV2>;
  /** GET /api/v2/sync/full-resync — full domain snapshot when the cursor is too old. */
  fullResync(): Promise<FullResyncPayload>;
}

/**
 * Shape the full-resync endpoint returns. Matches legacy
 * `FullResyncDataV2` but typed against our shared domain types. The server
 * sends `ShoppingListItemWithDetails`, but the engine only persists base
 * fields — display joins are rebuilt on the client.
 *
 * T12.0.16 — `household` carries the current `households` row-level state
 * the client mirrors (today: `store_preferences`). Optional so older server
 * builds without the channel still satisfy the type.
 */
export interface FullResyncPayload {
  ShoppingListItems: ShoppingListItem[];
  HouseholdProducts: HouseholdProduct[];
  ShoppingLists: ShoppingListLike[];
  household?: HouseholdResyncPayload;
}

/**
 * Household-row state the full-resync stream carries. Each field is the
 * post-LWW value the server holds for that column. The client replaces its
 * local mirror with these values verbatim.
 */
export interface HouseholdResyncPayload {
  /** `households.store_preferences` — whole-blob jsonb column. */
  store_preferences?: Record<string, unknown>;
}

/**
 * Structural shape for full-resync shopping lists. We accept anything with
 * an id; the full ShoppingList Zod schema lives in @shopista/shared/types
 * and will be used by the API client. Keeping this loose here avoids a hard
 * coupling during migration.
 */
export interface ShoppingListLike {
  id: string;
  [k: string]: unknown;
}

/** Dependency bundle — constructed at app entry, reused across syncs. */
export interface SyncEngineDeps {
  /** Full storage — the engine needs domain writes + event-log + sync-state. */
  storage: IStorage;
  /** Thin HTTP surface for sync endpoints (wraps the T3 HttpClient in prod). */
  api: SyncApi;
  /**
   * Clock reads. Returned value is milliseconds since epoch — matches
   * `Date.now()`. Tests pass a stub; prod passes `() => Date.now()`.
   */
  now: () => number;
  /**
   * Optional online check. When absent, the engine assumes always online.
   * When present and returning false, `push`/`pull`/`sync` short-circuit
   * to a no-op result (`skipped: 'offline'`). Web adapter will wire
   * `() => navigator.onLine`; native will wire its own.
   */
  isOnline?: () => boolean;
  /** Optional structured logger. No-op by default. */
  logger?: (msg: string, meta?: unknown) => void;
  /**
   * T12.0.16 — optional household-row event handler. Called once per
   * `HouseholdActionTypeV2` event the engine receives on pull (and once per
   * field during full-resync). The handler is expected to update whatever
   * the platform uses to mirror household state — for web, that's a
   * TanStack Query cache invalidation; for native, a SQLite write.
   *
   * No-op when absent — the engine still advances its cursor and logs the
   * event. Apps that haven't wired a mirror yet stay correct (next REST
   * read pulls the post-LWW value).
   */
  onHouseholdEvent?: (event: HouseholdEvent) => void | Promise<void>;
  /**
   * T12.0.17 fix #3 — optional shopping-list-item event handler. Called
   * once per `ItemActionTypeV2` event the engine successfully applies
   * (pull OR push-delivered via `applyOne`). Mirrors `onHouseholdEvent`'s
   * shape: handler is the platform's invalidation hook. For web, it
   * invalidates the TanStack Query keys backing `useActiveShoppingList`;
   * for native, it's a SQLite write or no-op.
   *
   * Skipped when LWW resolves remote-loses or the target row is missing —
   * see `applyShoppingListItemEvent`. On full-resync the engine fires the
   * callback once with `bulk: true` after seeding the items table so the
   * UI invalidates the cache once instead of N times.
   *
   * No-op when absent.
   */
  onShoppingListItemEvent?: (event: ShoppingListItemEvent) => void | Promise<void>;
  /**
   * T12.0.17 fix #3 — optional household-product event handler. Same
   * shape contract as `onShoppingListItemEvent`. The web provider wires
   * this to invalidate `keys.HouseholdProducts.all`.
   *
   * No-op when absent.
   */
  onHouseholdProductEvent?: (event: HouseholdProductEvent) => void | Promise<void>;
}

/**
 * Household-row event delivered to `SyncEngineDeps.onHouseholdEvent`.
 * `payload` is the whole-blob jsonb value the server applied; the client
 * replaces its local mirror with this verbatim per D2 whole-blob LWW.
 */
export interface HouseholdEvent {
  actionType: HouseholdActionTypeV2;
  payload: Record<string, unknown>;
  /** Server-stamped time the event landed. Useful for client-side logging. */
  timestamp: string;
}

/**
 * Shopping-list-item event delivered to
 * `SyncEngineDeps.onShoppingListItemEvent` after the engine has written
 * the change to local storage. T12.0.17 fix #3 — the web provider hooks
 * this to invalidate the TanStack Query caches the UI reads from
 * (`useActiveShoppingList`, `useShoppingLists`).
 *
 * The callback only fires when the apply actually changed local state —
 * LWW-stale and no-target events are skipped (the projection returned
 * `false`).
 *
 * `bulk: true` indicates a full-resync seeded many rows; the handler
 * should invalidate without inspecting `itemId` / `payload`. Both fields
 * are undefined in the bulk case.
 */
export interface ShoppingListItemEvent {
  actionType: ItemActionTypeV2;
  itemId?: string;
  payload?: Record<string, unknown>;
  timestamp: string;
  bulk?: true;
}

/**
 * HouseholdProduct event delivered to
 * `SyncEngineDeps.onHouseholdProductEvent`. Mirror of `ShoppingListItemEvent`
 * for the products table. T12.0.17 fix #3 — the web provider hooks this
 * to invalidate `keys.HouseholdProducts.all`.
 */
export interface HouseholdProductEvent {
  actionType: ProductActionTypeV2;
  productId?: string;
  payload?: Record<string, unknown>;
  timestamp: string;
  bulk?: true;
}

/** Outcome of a push attempt. */
export interface PushResult {
  pushed: number;
  conflicts: SyncConflictV2[];
  skipped?: 'offline' | 'empty';
}

/** Outcome of a pull attempt. */
export interface PullResult {
  applied: number;
  fullResyncPerformed: boolean;
  skipped?: 'offline';
}

/** Outcome of a combined sync (push + pull). */
export interface SyncResult {
  push: PushResult;
  pull: PullResult;
  skipped?: 'offline';
}

/** Public sync engine contract. */
export interface SyncEngine {
  /** Push local pending events to server. */
  push(identity: SyncIdentity): Promise<PushResult>;
  /** Pull server events since last cursor and apply to local storage. */
  pull(identity: SyncIdentity): Promise<PullResult>;
  /** Push then pull. Skips pull if push fails (avoids diverging further). */
  sync(identity: SyncIdentity): Promise<SyncResult>;
  /**
   * Apply a single server event to local storage.
   *
   * T12.0.17 D8 — the SSE subscriber feeds inbound events through this method
   * so the same projection path (LWW + writers) handles them whether they
   * arrived via `pull()` or via the push transport. Returns `true` when the
   * local state changed, `false` otherwise (stale LWW, no target, missing
   * record for an event that requires one).
   *
   * Cursor is NOT advanced by `applyOne` — push-delivered events do not
   * change the catch-up cursor; the next periodic `pull()` advances it.
   */
  applyOne(event: ServerEventV2): Promise<boolean>;
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

class SyncEngineImpl implements SyncEngine {
  constructor(private readonly deps: SyncEngineDeps) {}

  async push(identity: SyncIdentity): Promise<PushResult> {
    if (!this.online()) {
      this.log('push: skipped (offline)');
      return { pushed: 0, conflicts: [], skipped: 'offline' };
    }

    const pending = await readPendingEvents(this.deps.storage);
    if (pending.length === 0) {
      this.log('push: no pending events');
      return { pushed: 0, conflicts: [], skipped: 'empty' };
    }

    const cursor = await readSyncCursor(this.deps.storage, identity.deviceId);
    const request = this.buildRequest(identity, cursor, pending);

    const response = await this.deps.api.sync(request);

    // Legacy: mark pending events synced FIRST (server always processes events
    // before responding, so an ack means they're durable).
    await markEventsPushed(
      this.deps.storage,
      pending.map((e) => e.id),
    );

    // Push-only: intentionally skip applying newEvents here. The caller
    // typically follows with `pull()` which advances the cursor and applies.
    // We still log conflicts for observability.
    if (response.conflicts.length > 0) {
      this.log('push: server resolved conflicts (server_wins)', {
        count: response.conflicts.length,
      });
    }

    return { pushed: pending.length, conflicts: response.conflicts };
  }

  async pull(identity: SyncIdentity): Promise<PullResult> {
    if (!this.online()) {
      this.log('pull: skipped (offline)');
      return { applied: 0, fullResyncPerformed: false, skipped: 'offline' };
    }

    const cursor = await readSyncCursor(this.deps.storage, identity.deviceId);
    // Pull = sync with zero events. Backend uses the same endpoint for both
    // directions; legacy distinguished by event count.
    const request = this.buildRequest(identity, cursor, []);

    const response = await this.deps.api.sync(request);

    if (
      response.fullResyncRequired ||
      !(await this.deps.storage.isHouseholdSeeded(identity.householdId))
    ) {
      this.log('pull: fullResyncRequired — clearing domain data');
      await this.performFullResync(identity, response.serverTimestamp);
      return { applied: 0, fullResyncPerformed: true };
    }

    let applied = 0;
    for (const event of response.newEvents) {
      if (await this.applyServerEvent(event)) applied += 1;
    }

    await writeSyncCursor(
      this.deps.storage,
      identity.deviceId,
      response.serverTimestamp,
      this.deps.now(),
    );

    return { applied, fullResyncPerformed: false };
  }

  async sync(identity: SyncIdentity): Promise<SyncResult> {
    if (!this.online()) {
      this.log('sync: skipped (offline)');
      return {
        push: { pushed: 0, conflicts: [], skipped: 'offline' },
        pull: { applied: 0, fullResyncPerformed: false, skipped: 'offline' },
        skipped: 'offline',
      };
    }

    let pushResult: PushResult;
    try {
      pushResult = await this.push(identity);
    } catch (err) {
      // Push failed — do NOT attempt pull. Diverging state would be worse
      // than waiting for the caller to retry.
      this.log('sync: push failed, skipping pull', { error: describeError(err) });
      throw err;
    }

    const pullResult = await this.pull(identity);

    // After a successful round-trip, GC synced events from the pending log.
    await purgeSyncedEvents(this.deps.storage);

    return { push: pushResult, pull: pullResult };
  }

  /**
   * T12.0.17 D8 — apply one server-delivered event without the surrounding
   * pull-cursor mechanics. The SSE subscriber calls this for each push-
   * delivered event so the projection path stays identical to `pull()`.
   *
   * The `applyServerEvent` private method is the implementation; this
   * indirection keeps the interface explicit for external callers.
   */
  async applyOne(event: ServerEventV2): Promise<boolean> {
    return this.applyServerEvent(event);
  }

  // -------------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------------

  private buildRequest(
    identity: SyncIdentity,
    cursor: string,
    pending: readonly PendingEvent[],
  ): SyncRequestV2 {
    const events: ClientEventV2[] = pending.map(toClientEvent);
    return {
      householdId: identity.householdId,
      deviceId: identity.deviceId,
      userId: identity.userId,
      lastSyncedAt: cursor,
      events,
    };
  }

  private online(): boolean {
    return this.deps.isOnline ? this.deps.isOnline() : true;
  }

  private log(msg: string, meta?: unknown): void {
    this.deps.logger?.(msg, meta);
  }

  /**
   * Apply one server event to local storage. Returns true if the local
   * state changed. Order of checks mirrors legacy apply logic; any change
   * to action semantics should start in `backend/src/household/syncService.ts`.
   */
  private async applyServerEvent(event: ServerEventV2): Promise<boolean> {
    const { shoppingListItemId, householdproductId, actionType, newValue, timestamp } = event;

    if (isHouseholdActionType(actionType)) {
      // T12.0.16 — household-row event. Whole-blob LWW already applied
      // server-side (D2); the client just replaces its local mirror via
      // the platform-specific handler.
      await this.applyHouseholdEvent(actionType, newValue, timestamp);
      return true;
    }
    if (shoppingListItemId) {
      return this.applyShoppingListItemEvent(shoppingListItemId, actionType, newValue, timestamp);
    }
    if (householdproductId) {
      return this.applyHouseholdProductEvent(householdproductId, actionType, newValue, timestamp);
    }
    // Event with no target — skip. Legacy silently ignored; do the same.
    this.log('applyServerEvent: event has no target — skipping', { actionType });
    return false;
  }

  private async applyHouseholdEvent(
    actionType: HouseholdActionTypeV2,
    newValue: Record<string, unknown> | undefined,
    timestamp: string,
  ): Promise<void> {
    if (!newValue) {
      this.log('applyHouseholdEvent: event missing payload — skipping', { actionType });
      return;
    }
    const handler = this.deps.onHouseholdEvent;
    if (!handler) {
      this.log('applyHouseholdEvent: no handler wired — skipping', { actionType });
      return;
    }
    await handler({ actionType, payload: newValue, timestamp });
  }

  private async applyShoppingListItemEvent(
    itemId: string,
    actionType: ActionTypeV2,
    newValue: Record<string, unknown> | undefined,
    timestamp: string,
  ): Promise<boolean> {
    const { changed, invalidate } = await this.applyShoppingListItemEventInner(
      itemId,
      actionType,
      newValue,
      timestamp,
    );
    if (invalidate) {
      // T12.0.17 fix #3 — fire the cache-invalidation callback so the UI
      // (TanStack Query) refetches.
      //
      // BUG21 fix — `invalidate` is decoupled from `changed`. The REST
      // `ShoppingLists.active` cache the UI renders is a SEPARATE store from
      // local (Dexie) storage: REST is a full snapshot, while local is built
      // incrementally from a cursor and may NOT hold a row the REST cache
      // still shows. A delete / purchase / check event for a row absent from
      // local storage must therefore STILL invalidate the REST cache, even
      // though no local projection write happened. LWW-losing and no-target
      // events leave `invalidate` false, mirroring the contract documented on
      // `onShoppingListItemEvent`.
      const handler = this.deps.onShoppingListItemEvent;
      if (handler) {
        const event: ShoppingListItemEvent = {
          actionType: actionType as ItemActionTypeV2,
          itemId,
          timestamp,
          ...(newValue !== undefined ? { payload: newValue } : {}),
        };
        await handler(event);
      }
    }
    // `applied` accounting / `applyOne` boolean reflect that meaningful work
    // occurred — either a local write (`changed`) or a REST-cache refresh
    // (`invalidate`).
    return changed || invalidate;
  }

  private async applyShoppingListItemEventInner(
    itemId: string,
    actionType: ActionTypeV2,
    newValue: Record<string, unknown> | undefined,
    timestamp: string,
  ): Promise<ItemApplyResult> {
    const storage: IShoppingListItemStorage = this.deps.storage;
    const existing = await storage.getItem(itemId);

    // LWW: skip stale remote events (existing local record is newer).
    if (!shouldApplyRemote(existing, timestamp)) {
      this.log('applyShoppingListItemEvent: local wins (LWW) — skipping', {
        itemId,
        actionType,
      });
      return NO_APPLY;
    }

    const itemAction = actionType as ItemActionTypeV2;

    switch (itemAction) {
      case 'item_added': {
        // A duplicate add is a genuine no-op — neither write nor invalidate.
        if (existing || !newValue) return NO_APPLY;
        const record = buildNewShoppingListItem(itemId, newValue, timestamp);
        await storage.putItem(record);
        return APPLIED;
      }

      case 'item_checked':
        // BUG21 — always invalidate; only write when the row is local.
        if (existing) {
          await storage.updateItem(itemId, {
            checked: true,
            updated_at: timestamp,
          });
          return APPLIED;
        }
        return INVALIDATE_ONLY;

      case 'item_unchecked':
        if (existing) {
          await storage.updateItem(itemId, {
            checked: false,
            updated_at: timestamp,
          });
          return APPLIED;
        }
        return INVALIDATE_ONLY;

      case 'item_purchased': {
        if (existing) {
          const patch: Partial<ShoppingListItem> = {
            status: 'purchased',
            updated_at: timestamp,
          };
          const purchasedAt = stringField(newValue, 'purchased_at') ?? timestamp;
          patch.purchased_at = purchasedAt;
          const purchasedBy = stringField(newValue, 'purchased_by');
          if (purchasedBy !== undefined) patch.purchased_by = purchasedBy;
          await storage.updateItem(itemId, patch);
          return APPLIED;
        }
        return INVALIDATE_ONLY;
      }

      case 'item_deleted':
        // BUG21 — the deleted row may already be absent from local storage
        // (built from a cursor) while the REST snapshot still holds it. Delete
        // only when present (a delete of an absent row does nothing), but
        // always invalidate so the REST cache the Deals page renders drops its
        // stale "✓ On list" row.
        if (existing) {
          await storage.deleteItem(itemId);
          return APPLIED;
        }
        return INVALIDATE_ONLY;

      case 'item_updated':
      case 'item_quantity_changed':
      case 'item_notes_changed': {
        if (!existing || !newValue) return NO_APPLY;
        const patch: Partial<ShoppingListItem> = { updated_at: timestamp };
        copyIfPresent(newValue, patch, [
          // T27.2.7.9.3 — propagate the where-to-buy store anchor + its carried
          // deal to co-shoppers (the picker writes both). The server emits the
          // snake_case names; clears arrive as null.
          'selected_storelocation_id',
          'selected_retaileroffer_id',
          // T27.2.7.9.6 — the pinned flyer + its validity snapshot, so a pick (or
          // a clear) made by one co-shopper reaches the others live instead of
          // lagging until their next refetch (mirrors the store pick above).
          'flyerdeal_id',
          'flyerdeal_valid_from',
          'flyerdeal_valid_to',
          'quantity',
          'quantity_unit',
          'notes',
          'checked',
          'status',
        ]);
        await storage.updateItem(itemId, patch);
        return APPLIED;
      }

      default:
        // actionType was a product action; silently ignore here since the
        // outer dispatch already routed to myProduct branch when applicable.
        return NO_APPLY;
    }
  }

  private async applyHouseholdProductEvent(
    productId: string,
    actionType: ActionTypeV2,
    newValue: Record<string, unknown> | undefined,
    timestamp: string,
  ): Promise<boolean> {
    const changed = await this.applyHouseholdProductEventInner(
      productId,
      actionType,
      newValue,
      timestamp,
    );
    if (changed) {
      // T12.0.17 fix #3 — mirror `applyShoppingListItemEvent` so the
      // products-cache invalidation runs on push-delivered events too.
      const handler = this.deps.onHouseholdProductEvent;
      if (handler) {
        const event: HouseholdProductEvent = {
          actionType: actionType as ProductActionTypeV2,
          productId,
          timestamp,
          ...(newValue !== undefined ? { payload: newValue } : {}),
        };
        await handler(event);
      }
    }
    return changed;
  }

  private async applyHouseholdProductEventInner(
    productId: string,
    actionType: ActionTypeV2,
    newValue: Record<string, unknown> | undefined,
    timestamp: string,
  ): Promise<boolean> {
    const storage: IHouseholdProductStorage = this.deps.storage;
    const existing = await storage.getProduct(productId);

    if (!shouldApplyRemote(existing, timestamp)) {
      this.log('applyHouseholdProductEvent: local wins (LWW) — skipping', {
        productId,
        actionType,
      });
      return false;
    }

    const productAction = actionType as ProductActionTypeV2;

    switch (productAction) {
      case 'product_created': {
        if (existing || !newValue) return false;
        const record = buildNewHouseholdProduct(productId, newValue, timestamp);
        await storage.putProduct(record);
        return true;
      }

      case 'product_updated': {
        if (!existing || !newValue) return false;
        const patch: Partial<HouseholdProduct> = { updated_at: timestamp };
        // BUG22 — the inline 16-field list was extracted to the shared
        // canonical const so the emit + server projection cannot drift from
        // this reference projector. Membership/behavior unchanged.
        copyIfPresent(newValue, patch, PRODUCT_UPDATED_FIELDS);
        await storage.updateProduct(productId, patch);
        return true;
      }

      case 'product_deleted':
        if (!existing) return false;
        await storage.deleteProduct(productId);
        return true;

      case 'product_retailer_pref_added':
      case 'product_retailer_pref_removed': {
        // T12.0.1.4 — junction-row events. Local HouseholdProduct mutates only its
        // `retailer_preferences` array; the canonical row lives server-side
        // in `householdproducts_retailers_preferences`.
        if (!existing) return false;
        const retailerId = stringField(newValue, 'retailer_id');
        if (!retailerId) return false;
        const current = existing.retailer_preferences ?? [];
        const next =
          productAction === 'product_retailer_pref_added'
            ? current.some((p) => p.retailer_id === retailerId)
              ? current
              : [
                  ...current,
                  {
                    householdproduct_id: productId,
                    retailer_id: retailerId,
                    created_at: timestamp,
                  },
                ]
            : current.filter((p) => p.retailer_id !== retailerId);
        await storage.updateProduct(productId, {
          retailer_preferences: next,
          updated_at: timestamp,
        });
        return true;
      }

      default:
        return false;
    }
  }

  private async performFullResync(identity: SyncIdentity, serverTimestamp: string): Promise<void> {
    const payload = await this.deps.api.fullResync();

    // Clear household-scoped tables — event log stays so unsent local events
    // survive. Mirrors legacy `clearV2Data`.
    await this.deps.storage.clearDomainData();

    const listStorage: IShoppingListStorage = this.deps.storage;
    const itemStorage: IShoppingListItemStorage = this.deps.storage;
    const productStorage: IHouseholdProductStorage = this.deps.storage;

    for (const list of payload.ShoppingLists) {
      // Cast: ShoppingListLike is looser than ShoppingList; the API layer
      // (T3) validates shape. At runtime we trust the server.
      await listStorage.putList(list as Parameters<IShoppingListStorage['putList']>[0]);
    }
    if (payload.HouseholdProducts.length > 0) {
      await productStorage.putProducts(payload.HouseholdProducts);
      // T12.0.17 fix #3 — bulk-fire the products invalidation callback once
      // so the UI sees the freshly-seeded rows. Bulk shape is documented on
      // `HouseholdProductEvent.bulk`.
      if (this.deps.onHouseholdProductEvent) {
        await this.deps.onHouseholdProductEvent({
          actionType: 'product_updated',
          timestamp: serverTimestamp,
          bulk: true,
        });
      }
    }
    if (payload.ShoppingListItems.length > 0) {
      await itemStorage.putItems(payload.ShoppingListItems);
      // T12.0.17 fix #3 — bulk-fire the items invalidation callback once
      // after the items table is reseeded.
      if (this.deps.onShoppingListItemEvent) {
        await this.deps.onShoppingListItemEvent({
          actionType: 'item_updated',
          timestamp: serverTimestamp,
          bulk: true,
        });
      }
    }

    // T12.0.16 — household-row state. Server returns the post-LWW values;
    // the client replaces its mirror via the platform-specific handler so
    // a freshly-resynced device matches the server's current state without
    // replaying every historical household_event.
    if (payload.household?.store_preferences && this.deps.onHouseholdEvent) {
      await this.deps.onHouseholdEvent({
        actionType: 'household_store_preferences_changed',
        payload: payload.household.store_preferences,
        timestamp: serverTimestamp,
      });
    }

    const syncState: ISyncStateStorage = this.deps.storage;
    await writeSyncCursor(syncState, identity.deviceId, serverTimestamp, this.deps.now());
    await this.deps.storage.markHouseholdSeeded(identity.householdId);

    this.log('full-resync: applied', {
      lists: payload.ShoppingLists.length,
      products: payload.HouseholdProducts.length,
      items: payload.ShoppingListItems.length,
    });
  }
}

// ---------------------------------------------------------------------------
// Factory
// ---------------------------------------------------------------------------

/** Create a sync engine. Single entry point callers should use. */
export function createSyncEngine(deps: SyncEngineDeps): SyncEngine {
  return new SyncEngineImpl(deps);
}

// ---------------------------------------------------------------------------
// Helpers (module-private)
// ---------------------------------------------------------------------------

function stringField(source: Record<string, unknown> | undefined, key: string): string | undefined {
  if (!source) return undefined;
  const v = source[key];
  return typeof v === 'string' ? v : undefined;
}

/**
 * Copy any key in `keys` from `source` into `target` when present.
 * `Partial<T>` is expressed as `Record<string, unknown>` inside so TS
 * doesn't narrow to never for mixed-shape patches.
 */
function copyIfPresent<T>(
  source: Record<string, unknown>,
  target: Partial<T>,
  keys: readonly (keyof T & string)[],
): void {
  const mutableTarget = target as Record<string, unknown>;
  for (const key of keys) {
    if (key in source) {
      mutableTarget[key] = source[key];
    }
  }
}

/**
 * Construct a ShoppingListItem from a legacy `item_added` payload.
 * Defaults mirror legacy `applyShoppingListItemEvent::item_added`.
 */
function buildNewShoppingListItem(
  id: string,
  newValue: Record<string, unknown>,
  timestamp: string,
): ShoppingListItem {
  // T12.0.1.5: offer snapshot fields removed (offer_title_snapshot,
  // offer_size_snapshot, offer_price_snapshot, offer_storelocation_snapshot).
  // householdproduct_id is now required.
  return {
    id,
    shoppinglist_id: requireString(newValue, 'shoppinglist_id'),
    householdproduct_id: requireString(newValue, 'householdproduct_id'),
    // T27.2.7.9.3 — the where-to-buy store anchor.
    selected_storelocation_id: stringOrNull(newValue, 'selected_storelocation_id'),
    selected_retaileroffer_id: stringOrNull(newValue, 'selected_retaileroffer_id'),
    // T12.1.8.7 — openable "added-from" deal reference + validity snapshot.
    flyerdeal_id: stringOrNull(newValue, 'flyerdeal_id'),
    flyerdeal_valid_from: stringOrNull(newValue, 'flyerdeal_valid_from'),
    flyerdeal_valid_to: stringOrNull(newValue, 'flyerdeal_valid_to'),
    quantity: numberField(newValue, 'quantity') ?? 1,
    quantity_unit: stringOrNull(newValue, 'quantity_unit'),
    checked: booleanField(newValue, 'checked') ?? false,
    notes: stringOrNull(newValue, 'notes'),
    status: (stringField(newValue, 'status') as ShoppingListItem['status']) ?? 'pending',
    purchased_at: stringOrNull(newValue, 'purchased_at'),
    purchased_by: stringOrNull(newValue, 'purchased_by'),
    created_at: stringField(newValue, 'created_at') ?? timestamp,
    updated_at: timestamp,
  } as ShoppingListItem;
}

/**
 * Construct a HouseholdProduct from a legacy `product_created` payload.
 * Defaults mirror legacy `applyHouseholdProductEvent::product_created`.
 */
function buildNewHouseholdProduct(
  id: string,
  newValue: Record<string, unknown>,
  timestamp: string,
): HouseholdProduct {
  return {
    id,
    household_id: requireString(newValue, 'household_id'),
    display_name: requireString(newValue, 'display_name'),
    category_id: stringOrNull(newValue, 'category_id'),
    notes: stringOrNull(newValue, 'notes'),
    tags: (newValue['tags'] as string[] | undefined) ?? [],
    image_url: stringOrNull(newValue, 'image_url'),
    default_quantity: numberField(newValue, 'default_quantity') ?? 1,
    retailer_preferences: [],
    allow_substitutions: booleanField(newValue, 'allow_substitutions') ?? true,
    substitution_notes: stringOrNull(newValue, 'substitution_notes'),
    always_keep_stocked: booleanField(newValue, 'always_keep_stocked') ?? false,
    expected_price_value: numberField(newValue, 'expected_price_value'),
    expected_price_currency:
      (stringField(
        newValue,
        'expected_price_currency',
      ) as HouseholdProduct['expected_price_currency']) ?? 'CAD',
    expected_price_notes: stringOrNull(newValue, 'expected_price_notes'),
    seed_householdproduct_id: stringOrNull(newValue, 'seed_householdproduct_id'),
    seeded_at: stringOrNull(newValue, 'seeded_at'),
    created_by: stringOrNull(newValue, 'created_by'),
    created_at: stringField(newValue, 'created_at') ?? timestamp,
    updated_at: timestamp,
  } as HouseholdProduct;
}

function numberField(source: Record<string, unknown>, key: string): number | undefined {
  const v = source[key];
  return typeof v === 'number' ? v : undefined;
}

function booleanField(source: Record<string, unknown>, key: string): boolean | undefined {
  const v = source[key];
  return typeof v === 'boolean' ? v : undefined;
}

function requireString(source: Record<string, unknown>, key: string): string {
  const v = source[key];
  if (typeof v !== 'string') {
    throw new Error(`Sync payload missing required string field '${key}'`);
  }
  return v;
}

function stringOrNull(source: Record<string, unknown>, key: string): string | null | undefined {
  const v = source[key];
  if (v === null || v === undefined) return v;
  return typeof v === 'string' ? v : undefined;
}

function describeError(err: unknown): string {
  if (err instanceof Error) return `${err.name}: ${err.message}`;
  return String(err);
}
